import { BILLING_TIME_ZONE } from '@/services/billing/time-zone';

// Pluma's `formatDateTime` puts the year in every preset, so it can't produce the "Month D" (no
// year) half of the range label — this uses `Intl.DateTimeFormat` directly instead.
//
// Both read the billing time zone, not the viewer's. See `BILLING_TIME_ZONE`.
const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  timeZone: BILLING_TIME_ZONE,
});
const MONTH_DAY_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: BILLING_TIME_ZONE,
});

/**
 * The boundary when it names a real date. The account payload types both boundaries as required
 * numbers and sends 0 when there is no billing period, so 0 means absent, not the epoch.
 */
export function knownBoundary(boundary: number | undefined): number | undefined {
  if (boundary == null || boundary <= 0) {
    return undefined;
  }
  return boundary;
}

/**
 * The billing period as "Month D – Month D, YYYY", keeping the start year only when it differs
 * from the end year. Returns null while either boundary is unknown, so the caller can name the
 * period instead of printing a range it doesn't have.
 *
 * @param start - billing period start, as unix seconds
 * @param end - billing period end, as unix seconds
 */
export function formatPeriodRange(rawStart: number | undefined, rawEnd: number | undefined): string | null {
  const start = knownBoundary(rawStart);
  const end = knownBoundary(rawEnd);
  if (start == null || end == null) {
    return null;
  }

  const startDate = new Date(start * 1000);
  const endDate = new Date(end * 1000);
  const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
  const startFormatter = sameYear ? MONTH_DAY_FORMATTER : MONTH_DAY_YEAR_FORMATTER;

  return `${startFormatter.format(startDate)} – ${MONTH_DAY_YEAR_FORMATTER.format(endDate)}`;
}

/**
 * A boundary as "Month D", in the billing time zone like the range above. Null while the date is unknown, so the caller
 * can name the figure without dating it.
 *
 * @param date - unix seconds
 */
export function formatMonthDay(date: number | undefined): string | null {
  const known = knownBoundary(date);
  if (known == null) {
    return null;
  }

  return MONTH_DAY_FORMATTER.format(new Date(known * 1000));
}

/** A date as "October 12, 2026", or null when it is not known. */
export function formatMonthDayYear(date: number | undefined): string | null {
  const known = knownBoundary(date);
  if (known == null) {
    return null;
  }

  return MONTH_DAY_YEAR_FORMATTER.format(new Date(known * 1000));
}

const SECONDS_PER_DAY = 86_400;

export type PeriodDays = {
  /** Which day of the period today is, counting the first day as day 1. */
  dayNumber: number;
  /** How many days the period covers. */
  totalDays: number;
};

/**
 * How far into the period today is, e.g. day 19 of 31. Null while either boundary is unknown.
 *
 * Measured from the start to the charge. The vendors disagree on what the period *end* means —
 * Ordway builds it as the last instant inside the period, everyone else as the rollover — but the
 * charge means the same for both.
 *
 * Whole days come from the raw seconds, rounded, and not from calendar arithmetic. An Ordway date is
 * anchored to UTC midnight, while a Stripe or Metronome instant falls at the time of day the
 * subscription renews. Rounding reads that part-day as the day it belongs to.
 *
 * The day number stays inside the period, so a read before it opens shows day 1 and a read after it
 * closes shows the last day.
 *
 * @param now - the moment to place in the period, for tests
 */
export function periodDays(
  rawStart: number | undefined,
  rawChargeDate: number | undefined,
  now: Date = new Date(),
): PeriodDays | null {
  const start = knownBoundary(rawStart);
  const chargeDate = knownBoundary(rawChargeDate);
  if (start == null || chargeDate == null) {
    return null;
  }

  const totalDays = Math.round((chargeDate - start) / SECONDS_PER_DAY);
  if (totalDays < 1) {
    return null;
  }

  const elapsed = Math.floor((now.getTime() / 1000 - start) / SECONDS_PER_DAY);
  const dayNumber = Math.min(Math.max(elapsed + 1, 1), totalDays);

  return { dayNumber, totalDays };
}

/** Stands in for the dates until the billing period is known. */
const UNDATED_PERIOD = 'this cycle';

type PeriodLabelInput = {
  /** The full range, or null when it is unknown or its start is not reliable. */
  range: string | null;
  /** The period end as "October 3, 2026", or null when it is unknown. */
  endDate: string | null;
  isStartReliable: boolean;
};

/**
 * The period as the billing period card states it. The full range when the start is reliable, the
 * end alone when it is not, and "this cycle" when no dates are known. A dated label names its zone,
 * which can differ from the viewer's.
 */
export function periodLabel({ range, endDate, isStartReliable }: PeriodLabelInput): string {
  if (isStartReliable && range != null) {
    return `${range} (${BILLING_TIME_ZONE})`;
  }
  if (!isStartReliable && endDate != null) {
    return `Ends ${endDate} (${BILLING_TIME_ZONE})`;
  }
  return UNDATED_PERIOD;
}
